import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserRound, Wallet } from 'lucide-react';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT, KIOSK_NAV_APPEAR_SESSION_KEY } from '@/services/apiService';
import { playClickSound, playSound, isSoundEnabled, SOUND_NAMES } from '@/utils/soundUtils';
import { navigationFromState } from '@/utils/navigateBack';
import { BALANCE_REFRESH_EVENT } from '@/utils/balanceRefresh';
import { useAuthModal } from '@/contexts/AuthModalContext';
import KioskNotificationBell from '@/components/KioskNotificationBell';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Avatar, Button, CountBadge, Money, Spinner, TopTabs, cx, focusRing } from '@/components/ui';
import { useSupportUnread } from '@/features/support/hooks/useSupportUnread';
import { useTranslation } from 'react-i18next';
import { useCountUp } from '@/hooks/useCountUp';
import { ANIMATION_REGISTRY, getSharedShineClockStyle } from '@/config/animationRegistry';
import { useUiScale } from '@/contexts/DeviceModeContext';
import { TABS, TAB_ICONS, activeTab } from '@/layouts/navTabs';
import HeaderAddFunds from './HeaderAddFunds';
import vaultPlayLogo from '@/assets/brand/vault-play-logo.png';
import CampaignBonusStrip from '@/features/bonus/CampaignBonusStrip';

export interface HeaderProps {
  /** @deprecated Prototype chrome is static — back/breadcrumb ignored. */
  back?: boolean;
  /** @deprecated ignored */
  backTo?: string;
  type: 'logged' | 'normal';
  size?: 'xs' | 'm' | 'l' | 'XL' | 's';
  /** When true, header fits compact chrome height (`--wf-header-h`). Used on Home / fixed shells. */
  compact?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
  /** Show the avatar (shortcut to Me, wide layouts) in the right section. Default true. */
  showUserInfo?: boolean;
  /** Show balance box in right section (logged only). Default true. */
  showBalance?: boolean;
  /** Optional custom right slot (e.g. EDIT button). When set, replaces Balance in right section. */
  rightSlot?: React.ReactNode;
  /** When true with type normal, show admin broadcast bell (e.g. logged-in user on home). Logged type always shows the bell. */
  showNotificationBell?: boolean;
}

/**
 * App header on the v2 chrome surface: brand mark, the four top tabs (Play / Money / Help / Me,
 * wide layouts; BottomNav on compact) and the money actions — balance chip (→ Money), "+ Add money"
 * (→ the guided flow), notifications, the bonus badge and, on wide layouts, the avatar (→ Me).
 * Signed out: language and Sign in. Supports both logged and normal (public) types.
 */
export const Header: React.FC<HeaderProps & React.HTMLAttributes<HTMLElement>> = ({
  back: _back,
  backTo: _backTo,
  type = 'normal',
  size: _size = 'XL',
  compact = false,
  className = '',
  title: _title,
  subtitle: _subtitle,
  showUserInfo = true,
  showBalance = true,
  rightSlot,
  showNotificationBell = false,
  style,
  ...restProps
}) => {
  const { t } = useTranslation();
  const { layout } = useUiScale();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, requireAuth, openAuthModal } = useAuthModal();

  const showBell = type === 'logged' || showNotificationBell;
  // Balance/account UI applies to logged shells and to authed users on the public shell
  const authedUi = type === 'logged' || isAuthenticated;
  const showBalanceUi = showBalance && authedUi;

  const [balance, setBalance] = useState<number>(0);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceDelta, setBalanceDelta] = useState<number | null>(null);
  const [showBalanceAttention, setShowBalanceAttention] = useState(false);
  /** Count-up display for the balance chip — smooths abrupt value changes */
  const animatedBalance = useCountUp(balance, ANIMATION_REGISTRY.countUp.headerBalanceMs);
  /** Tickets with a support reply the Player has not opened yet (0 for guests). */
  const supportUnread = useSupportUnread();
  const previousBalanceRef = useRef<number | null>(null);
  const balanceAttentionTimeoutRef = useRef<number | null>(null);
  const lastBalanceSoundAtRef = useRef<number>(0);
  const [navAppearGen, setNavAppearGen] = useState(0);
  const prevNavTabCountRef = useRef<number | null>(null);
  const prevAuthedRef = useRef(isAuthenticated);
  const navAppearTriggeredRef = useRef(0);
  const loginAppearDoneRef = useRef(false);

  /** Phase-lock Sign in gleam to the same wall clock as Home grid shine. */
  const guestAuthShineStyle = useMemo(
    () => getSharedShineClockStyle() as React.CSSProperties,
    // Recapture phase when the guest CTA mounts again (Header stays mounted across auth).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated]
  );

  const triggerNavAppear = useCallback(() => {
    setNavAppearGen((gen) => {
      const next = gen + 1;
      navAppearTriggeredRef.current = next;
      return next;
    });
    sessionStorage.removeItem(KIOSK_NAV_APPEAR_SESSION_KEY);
  }, []);

  const handleNavAppearComplete = useCallback(() => {
    sessionStorage.removeItem(KIOSK_NAV_APPEAR_SESSION_KEY);
  }, []);

  const applyBalanceWithAttention = (nextBalance: number) => {
    const previous = previousBalanceRef.current;
    setBalance(nextBalance);
    previousBalanceRef.current = nextBalance;

    if (previous === null) return;
    const delta = Number((nextBalance - previous).toFixed(2));
    if (Math.abs(delta) < 0.01) return;

    setBalanceDelta(delta);
    setShowBalanceAttention(true);
    if (balanceAttentionTimeoutRef.current) {
      window.clearTimeout(balanceAttentionTimeoutRef.current);
    }
    balanceAttentionTimeoutRef.current = window.setTimeout(() => {
      setShowBalanceAttention(false);
      setBalanceDelta(null);
    }, 2200);

    const now = Date.now();
    if (isSoundEnabled() && now - lastBalanceSoundAtRef.current > 2500) {
      playSound(delta > 0 ? SOUND_NAMES.MONEY_UP : SOUND_NAMES.MONEY_DOWN);
      lastBalanceSoundAtRef.current = now;
    }
  };

  // Load balance when the pill is visible; re-fetch on account switch (token change).
  useEffect(() => {
    if (!showBalanceUi) {
      setBalance(0);
      setBalanceLoading(false);
      previousBalanceRef.current = null;
      setShowBalanceAttention(false);
      setBalanceDelta(null);
      return;
    }

    let cancelled = false;

    const resetLocalBalance = () => {
      previousBalanceRef.current = null;
      setShowBalanceAttention(false);
      setBalanceDelta(null);
      setBalance(0);
    };

    const loadBalance = async () => {
      try {
        setBalanceLoading(true);
        const response = await apiService.get<{
          balance: number;
          currency: string;
          lastUpdated: string;
        }>('/payment/balance', true, true);

        if (cancelled) return;

        if (response.success && response.data) {
          const data = response.data as any;
          const balanceValue = data.balance ?? data.data?.balance ?? 0;
          applyBalanceWithAttention(balanceValue);
        }
      } catch (error) {
        if (!cancelled) console.error('Error loading balance:', error);
      } finally {
        if (!cancelled) setBalanceLoading(false);
      }
    };

    void loadBalance();

    const onAuthTokenChanged = () => {
      if (!apiService.isAuthenticated()) {
        resetLocalBalance();
        setBalanceLoading(false);
        return;
      }
      // Drop prior account's painted balance immediately, then force-fetch.
      resetLocalBalance();
      void loadBalance();
    };

    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuthTokenChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuthTokenChanged);
    };
  }, [showBalanceUi]);

  // When another part of the app refreshes balance (e.g. Wallet page), update header balance too
  useEffect(() => {
    if (!showBalanceUi) return;

    const onBalanceShouldRefresh = async () => {
      try {
        setBalanceLoading(true);
        const response = await apiService.get<{
          balance: number;
          currency: string;
          lastUpdated: string;
        }>('/payment/balance', true, true);

        if (response.success && response.data) {
          const data = response.data as any;
          const balanceValue = data.balance ?? data.data?.balance ?? 0;
          applyBalanceWithAttention(balanceValue);
        }
      } catch (error) {
        console.error('Error refreshing header balance:', error);
      } finally {
        setBalanceLoading(false);
      }
    };

    window.addEventListener(BALANCE_REFRESH_EVENT, onBalanceShouldRefresh);
    return () => window.removeEventListener(BALANCE_REFRESH_EVENT, onBalanceShouldRefresh);
  }, [showBalanceUi]);

  const handleReloadBalance = async () => {
    playClickSound();
    try {
      setBalanceLoading(true);
      const response = await apiService.get<{
        balance: number;
        currency: string;
        lastUpdated: string;
      }>('/payment/balance', true, true);

      if (response.success && response.data) {
        const data = response.data as any;
        const balanceValue = data.balance ?? data.data?.balance ?? 0;
        applyBalanceWithAttention(balanceValue);
      }
    } catch (error) {
      console.error('Error reloading balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (balanceAttentionTimeoutRef.current) {
        window.clearTimeout(balanceAttentionTimeoutRef.current);
      }
    };
  }, []);

  const handleTabChange = useCallback(
    (id: string) => {
      playClickSound();
      const tab = TABS.find((item) => item.id === id);
      if (!tab) return;
      // Already on this tab's route — don't push history / remount page animations.
      if (location.pathname === tab.to) return;
      // Money and Me ask guests to sign in; Play and Help are open.
      if (tab.gated) requireAuth(() => navigate(tab.to));
      else navigate(tab.to);
    },
    [navigate, requireAuth, location.pathname]
  );

  const supportUnreadLabel = t('supportDesk.page.unreadBadgeLabel', { count: supportUnread });

  // The same four tabs for guests and players (Money and Me ask guests to sign in).
  // Help carries the count of support replies waiting.
  const navTabs = TABS.map((tab) => ({
    id: tab.id,
    label: t(tab.labelKey),
    icon: TAB_ICONS[tab.id],
    tourId: tab.tourId,
    badge:
      tab.id === 'help' && supportUnread > 0 ? (
        <CountBadge count={supportUnread} max={9} label={supportUnreadLabel} />
      ) : undefined,
  }));
  const activeTabId = activeTab(location.pathname);

  useLayoutEffect(() => {
    if (!isAuthenticated) {
      loginAppearDoneRef.current = false;
      prevAuthedRef.current = false;
      prevNavTabCountRef.current = navTabs.length;
      return;
    }

    const prevCount = prevNavTabCountRef.current;
    const expanded = navTabs.length >= 4 && prevCount !== null && prevCount < 4;
    const justAuthed = !prevAuthedRef.current && isAuthenticated && navTabs.length >= 4;
    const pendingFromLogin =
      prevCount === null &&
      navTabs.length >= 4 &&
      sessionStorage.getItem(KIOSK_NAV_APPEAR_SESSION_KEY) === '1';

    if ((expanded || justAuthed || pendingFromLogin) && !loginAppearDoneRef.current) {
      loginAppearDoneRef.current = true;
      triggerNavAppear();
    }

    prevAuthedRef.current = isAuthenticated;
    prevNavTabCountRef.current = navTabs.length;
  }, [isAuthenticated, navTabs.length, triggerNavAppear]);

  return (
    <header
      className={cx(
        // Static chrome: brand + tabs + actions — no back / breadcrumb.
        // overflow-x only: badges and focus rings may still escape vertically.
        'flex w-full items-center overflow-x-clip overflow-y-visible',
        'border-b border-edge-subtle bg-chrome backdrop-blur-md px-(--proto-hd-pad-x)',
        compact
          ? 'relative z-chrome min-h-(--wf-header-h)'
          : 'sticky top-0 z-chrome min-h-[calc(var(--proto-hd-action-h)+2*var(--proto-hd-pad-y))] py-(--proto-hd-pad-y)',
        className
      )}
      style={style}
      {...restProps}
    >
      <div className="flex w-full min-w-0 items-center gap-(--proto-hd-gap)">
        <button
          type="button"
          onClick={() => {
            playClickSound();
            navigate('/');
          }}
          className={cx('flex shrink-0 items-center gap-2 rounded-control', focusRing)}
          aria-label={t('header.goHome', 'Go to home')}
        >
          {/* The brand name sits next to it (and the button has its own label), so the logo is decorative. */}
          <img
            src={vaultPlayLogo}
            alt=""
            className="size-(--proto-hd-logo) shrink-0 rounded-control object-cover"
            draggable={false}
          />
          {/* hd-brand-text: hidden on the compact layout and narrow tablets by the chrome rules in globals.css */}
          <span className="hd-brand-text whitespace-nowrap font-display text-title font-bold leading-none tracking-tight text-primary">
            {t('header.appName', 'Vault Play')}
          </span>
        </button>

        {/* Nav sits immediately after brand (left-aligned). Hidden on the compact layout (BottomNav replaces it). */}
        <TopTabs
          tabs={navTabs}
          activeId={activeTabId}
          onChange={handleTabChange}
          waveGeneration={navAppearGen}
          onAppearComplete={handleNavAppearComplete}
          className="ml-3 shrink-0"
        />

        <div className="ml-auto flex min-w-0 shrink items-center gap-1 @bp-sm:gap-1.5">
          {authedUi && <CampaignBonusStrip />}

          {showBalanceUi && rightSlot == null && (
            <div
              data-tour-id={showBalanceUi ? 'header.balance' : undefined}
              className={cx(
                'relative shrink-0 transition-transform duration-300',
                showBalanceAttention ? 'scale-105' : 'scale-100'
              )}
            >
              {balanceDelta !== null && (
                <span
                  className={cx(
                    'pointer-events-none absolute -right-2 -top-2 z-10 rounded-control border border-edge bg-raised px-1.5 shadow-raised',
                    showBalanceAttention && 'animate-pulse'
                  )}
                >
                  <Money
                    amount={Math.abs(balanceDelta)}
                    direction={balanceDelta > 0 ? 'in' : 'out'}
                    sign
                    size="small"
                  />
                </span>
              )}
              {/* Full amount, never truncated: the chip grows with the Money text and does not shrink. */}
              <button
                type="button"
                onClick={() => {
                  playClickSound();
                  requireAuth(() => navigate('/money'));
                }}
                onDoubleClick={handleReloadBalance}
                disabled={balanceLoading}
                aria-label={t('header.myMoney')}
                className={cx(
                  'inline-flex h-10 items-center justify-center rounded-control border bg-action-neutral px-2 text-amount-out @bp-sm:px-3',
                  'transition-colors duration-(--k-dur-fast) hover:bg-action-neutral-hover disabled:opacity-70',
                  showBalanceAttention ? 'border-amount-out' : 'border-edge-subtle hover:border-edge',
                  focusRing
                )}
              >
                {balanceLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Money amount={animatedBalance} direction="out" size="small" />
                )}
              </button>
            </div>
          )}

          {authedUi && (
            <HeaderAddFunds
              onClick={() => {
                requireAuth(() => navigate('/money/add', navigationFromState(location.pathname)));
              }}
            />
          )}

          {showBell ? (
            <div data-tour-id="header.bell" className="shrink-0">
              <KioskNotificationBell compact={compact} />
            </div>
          ) : null}

          {/* Signed in, language lives on Me. */}
          {!isAuthenticated && <LanguageSwitcher compact={compact} />}

          {type === 'logged' && rightSlot != null && rightSlot}

          {type === 'normal' && !isAuthenticated && (
            <Button
              variant="primary"
              size="md"
              icon={<Wallet />}
              // btn-shine: the periodic gleam, phase-locked to the Home grid shine clock
              className="btn-shine shrink-0"
              style={guestAuthShineStyle}
              aria-label={t('header.openMyWallet', 'Sign in / Register')}
              onClick={() => {
                openAuthModal('signin');
              }}
            >
              {t('header.openMyWallet', 'Sign in / Register')}
            </Button>
          )}

          {/* Wide layouts: the avatar is a shortcut to Me (compact layouts have the Me tab below). */}
          {authedUi && showUserInfo && layout === 'wide' && (
            <button
              type="button"
              onClick={() => {
                playClickSound();
                requireAuth(() => navigate('/me'));
              }}
              aria-label={t('header.tabs.me')}
              data-tour-id="header.me"
              className={cx('relative inline-flex shrink-0 rounded-full transition-opacity hover:opacity-90', focusRing)}
            >
              <Avatar icon={<UserRound strokeWidth={2.2} />} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
